import { X, Minus, Maximize2, GripHorizontal, Keyboard } from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageContext';
import { EngiCoroLogo } from '@/components/workflow/EngiCoroLogo';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useDraggablePanel } from '@/hooks/useDraggablePanel';

const SHORTCUT_GROUPS = [
  {
    title: 'File Operations',
    shortcuts: [
      { keys: ['Ctrl', 'S'], description: 'Save workflow' },
    ],
  },
  {
    title: 'Edit',
    shortcuts: [
      { keys: ['Ctrl', 'Z'], description: 'Undo' },
      { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['Ctrl', 'Y'], description: 'Redo (alternative)' },
      { keys: ['Delete'], description: 'Delete selected block' },
      { keys: ['Backspace'], description: 'Delete selected block' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['Escape'], description: 'Deselect / exit mode' },
      { keys: ['Scroll'], description: 'Pan canvas' },
      { keys: ['Ctrl', 'Scroll'], description: 'Zoom in/out' },
      { keys: ['Click block'], description: 'Select block' },
      { keys: ['Double-click group'], description: 'Enter layer' },
    ],
  },
  {
    title: 'Canvas',
    shortcuts: [
      { keys: ['Drag from sidebar'], description: 'Add new block' },
      { keys: ['Click + drag block'], description: 'Move block' },
      { keys: ['Click connector'], description: 'Select connector' },
    ],
  },
];

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded bg-muted border border-border text-[11px] font-mono font-medium text-foreground shadow-sm">
      {children}
    </kbd>
  );
}

interface HelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
  const { t } = useTranslation();
  const { position, folded, setFolded, panelRef, dragHandlers } = useDraggablePanel({
    open,
    desktopInitialPosition: { x: 40, y: 80 },
  });

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      style={{ left: position.x, top: position.y }}
      className="fixed z-50 shadow-2xl rounded-lg border border-border bg-background flex flex-col"
      // Prevent canvas interactions from leaking through
      onClick={(e) => e.stopPropagation()}
    >
      {/* Title bar – draggable */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-muted rounded-t-lg cursor-grab active:cursor-grabbing select-none border-b border-border"
        {...dragHandlers}
      >
        <div className="flex items-center gap-2">
          <GripHorizontal className="w-4 h-4 text-muted-foreground" />
          <EngiCoroLogo className="h-10" />
          <span className="text-sm font-semibold truncate">{t('helpTitle')}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setFolded(!folded)}>
            {folded ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onOpenChange(false)}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Content – collapsible */}
      {!folded && (
        <div className="w-[min(90vw,640px)] max-h-[60vh] flex flex-col">
          <Tabs defaultValue="getting-started" className="w-full px-3 pt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="getting-started" className="text-xs sm:text-sm">{t('gettingStartedTab')}</TabsTrigger>
              <TabsTrigger value="shortcuts" className="text-xs sm:text-sm">
                <Keyboard className="w-3.5 h-3.5 mr-1 hidden sm:inline-block" />Shortcuts
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[45vh] mt-3 pb-3">
              {/* Getting Started */}
              <TabsContent value="getting-started" className="mt-0 space-y-4 pr-4">
                <h3 className="text-lg font-semibold">{t('gettingStartedTitle')}</h3>
                <p className="text-muted-foreground">{t('gettingStartedDesc')}</p>
                <div className="space-y-3">
                  {([
                    ['gsWhatIsTitle', 'gsWhatIsDesc'],
                    ['gsAddBlocksTitle', 'gsAddBlocksDesc'],
                    ['gsConnectTitle', 'gsConnectDesc'],
                    ['gsGroupTitle', 'gsGroupDesc'],
                    ['gsEditTitle', 'gsEditDesc'],
                    ['gsLayersTitle', 'gsLayersDesc'],
                    ['gsSaveLoadTitle', 'gsSaveLoadDesc'],
                    ['gsImportExportTitle', 'gsImportExportDesc'],
                    ['gsAiTitle', 'gsAiDesc'],
                    ['gsKeyboardTitle', 'gsKeyboardShortcuts'],
                  ] as const).map(([titleKey, descKey]) => (
                    <div key={titleKey} className="p-4 rounded-lg bg-muted border border-border/50">
                      <h4 className="font-medium text-sm mb-1.5">{t(titleKey)}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{t(descKey)}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Keyboard Shortcuts */}
              <TabsContent value="shortcuts" className="mt-0 space-y-4 pr-4">
                <h3 className="text-lg font-semibold">Keyboard Shortcuts</h3>
                <p className="text-muted-foreground text-sm">Quick reference for all available shortcuts. On macOS, use <Kbd>⌘</Kbd> instead of <Kbd>Ctrl</Kbd>.</p>
                <div className="space-y-4">
                  {SHORTCUT_GROUPS.map((group) => (
                    <div key={group.title} className="rounded-lg border border-border overflow-hidden">
                      <div className="px-3 py-2 bg-muted/50 border-b border-border">
                        <h4 className="text-sm font-semibold">{group.title}</h4>
                      </div>
                      <div className="divide-y divide-border">
                        {group.shortcuts.map((shortcut, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors">
                            <span className="text-sm text-muted-foreground">{shortcut.description}</span>
                            <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                              {shortcut.keys.map((key, j) => (
                                <span key={j} className="flex items-center gap-1">
                                  {j > 0 && <span className="text-muted-foreground text-xs">+</span>}
                                  <Kbd>{key}</Kbd>
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>


            </ScrollArea>
          </Tabs>
        </div>
      )}
    </div>
  );
}
